import { useMemo, useState, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Inbox } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useReferralsList,
  useReferralAttachmentSummary,
  type ReferralAssignmentFilter,
  type ReferralListFilters,
  type ReferralListItem,
  type ReferralStatus,
} from '@/hooks/useReferrals';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { MedcomReferralImportModal } from '@/components/referrals/MedcomReferralImportModal';
import { AttachmentStatusBadge } from '@/components/referrals/AttachmentStatusBadge';
import { Banner } from '@/components/ui/Banner';
import { ColumnSortHeader } from '@/components/ui/ColumnSortHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { CodeChip, FilterChip, StatusChip, type StatusTone } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { PageLayout } from '@/components/ui/PageLayout';
import { Skeleton } from '@/components/ui/Skeleton';
import { ResponsiveTable, type TableColumn } from '@/components/ui/Table';
import { formatReferralDate } from '@/lib/referral/referralDateTime';
import {
  DEFAULT_REFERRAL_LIST_SORT,
  nextReferralListSort,
  sortReferrals,
  type ReferralListSort,
  type ReferralSortColumn,
} from '@/lib/referralListSort';
import { TYPE, TYPE_TABULAR, UI_SECONDARY } from '@/lib/typography';
import { cn } from '@/utils/cn';

const STATUSES: Array<ReferralStatus | 'all'> = ['all', 'new', 'accepted', 'declined'];
const ASSIGNMENTS: ReferralAssignmentFilter[] = ['all', 'unassigned', 'assigned'];

export function Referrals() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<ReferralStatus | 'all'>('all');
  const [assignmentFilter, setAssignmentFilter] = useState<ReferralAssignmentFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [medcomOpen, setMedcomOpen] = useState(false);
  const [sort, setSort] = useState<ReferralListSort>(DEFAULT_REFERRAL_LIST_SORT);

  const filters = useMemo<ReferralListFilters>(
    () => ({
      status: statusFilter,
      assignment: assignmentFilter,
      search: searchTerm.trim() || undefined,
    }),
    [statusFilter, assignmentFilter, searchTerm],
  );

  const { data, isLoading, error, refetch } = useReferralsList(activeClinicId, filters);
  const { data: members } = useClinicMembers(activeClinicId);
  const { data: attachmentSummary } = useReferralAttachmentSummary(
    referralsHaveMedcom(data) ? activeClinicId : null,
  );
  const sortedReferrals = useMemo(
    () =>
      sortReferrals(data ?? [], sort, {
        medcomLabel: t('referrals.medcom_badge'),
        assigneeLabel: (referral) => {
          if (!referral.assigned_clinician_id) return '';
          return (
            members?.find((member) => member.user_id === referral.assigned_clinician_id)?.name ??
            t('referrals.assigned_unknown')
          );
        },
      }),
    [data, sort, members, t],
  );

  const sortHeader = (column: ReferralSortColumn, label: string) => {
    const active = sort.column === column;
    return (
      <ColumnSortHeader
        label={label}
        active={active}
        direction={sort.direction}
        onClick={() => setSort((current) => nextReferralListSort(current, column))}
        ariaLabel={
          active
            ? t(sort.direction === 'asc' ? 'referrals.sorted_asc' : 'referrals.sorted_desc', {
                column: label,
              })
            : t('referrals.sort_by', { column: label })
        }
      />
    );
  };

  const sortableColumns: { id: ReferralSortColumn; label: string }[] = [
    { id: 'name', label: t('referrals.col_name') },
    { id: 'referrer', label: t('referrals.col_referrer') },
    { id: 'received', label: t('referrals.col_received') },
    { id: 'status', label: t('referrals.col_status') },
    { id: 'assigned', label: t('referrals.col_assigned') },
  ];

  const newButton = (
    <Button onClick={() => navigate('/henvisninger/new')} disabled={!activeClinicId}>
      {t('referrals.new_referral')}
    </Button>
  );

  const columns: TableColumn<ReferralListItem>[] = [
    {
      id: 'name',
      header: sortHeader('name', t('referrals.col_name')),
      sortDirection: ariaSort(sort, 'name'),
      cell: (r) => <NameCell referral={r} />,
    },
    {
      id: 'cpr',
      header: t('referrals.col_cpr'),
      cell: (r) =>
        r.has_cpr ? (
          <StatusChip tone="completed">{t('referrals.col_cpr')}</StatusChip>
        ) : (
          <span className="text-ds-muted">—</span>
        ),
    },
    {
      id: 'referrer',
      header: sortHeader('referrer', t('referrals.col_referrer')),
      sortDirection: ariaSort(sort, 'referrer'),
      cell: (r) => <ReferrerCell referral={r} summary={attachmentSummary?.get(r.id)} />,
    },
    {
      id: 'received',
      header: sortHeader('received', t('referrals.col_received')),
      sortDirection: ariaSort(sort, 'received'),
      cell: (r) => <span className={TYPE_TABULAR}>{formatReferralDate(r.received_at) ?? '—'}</span>,
    },
    {
      id: 'status',
      header: sortHeader('status', t('referrals.col_status')),
      sortDirection: ariaSort(sort, 'status'),
      cell: (r) => <ReferralStatusBadge status={r.status} />,
    },
    {
      id: 'info',
      header: t('referrals.col_info_progress'),
      cell: (r) => <InfoCollectionProgress referral={r} />,
      hideOnMobile: true,
    },
    {
      id: 'assigned',
      header: sortHeader('assigned', t('referrals.col_assigned')),
      sortDirection: ariaSort(sort, 'assigned'),
      cell: (r) =>
        r.assigned_clinician_id
          ? (members?.find((m) => m.user_id === r.assigned_clinician_id)?.name ??
            t('referrals.assigned_unknown'))
          : '—',
      hideOnMobile: true,
    },
  ];

  return (
    <PageLayout>
      <div className="flex min-h-10 w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap nav:items-center">
        <div className="shrink-0">
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('referrals.title')}</h1>
        </div>
        <div className="w-full min-w-0 max-w-search nav:w-[420px] nav:min-w-[12.5rem] nav:flex-1">
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('referrals.search_placeholder')}
            aria-label={t('referrals.search_placeholder')}
          />
        </div>
        <div className="flex flex-wrap items-center gap-s3 nav:shrink-0">
          <div
            className="flex flex-wrap items-center gap-s2"
            role="group"
            aria-label={t('referrals.col_status')}
          >
            {STATUSES.map((s) => (
              <FilterChip key={s} pressed={statusFilter === s} onClick={() => setStatusFilter(s)}>
                {t(`referrals.status_${s}`)}
              </FilterChip>
            ))}
          </div>
          <div
            className="flex flex-wrap items-center gap-s2"
            role="group"
            aria-label={t('referrals.col_assigned')}
          >
            {ASSIGNMENTS.map((a) => (
              <FilterChip
                key={a}
                pressed={assignmentFilter === a}
                onClick={() => setAssignmentFilter(a)}
              >
                {t(`referrals.assignment_${a}`)}
              </FilterChip>
            ))}
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setMedcomOpen(true)}
            disabled={!activeClinicId}
          >
            {t('referrals.medcom_fetch')}
          </Button>
          {newButton}
        </div>
      </div>

      <div className="mt-s5">
        {error ? (
          <div className="mb-s4">
            <Banner
              tone="error"
              action={
                <Button type="button" variant="secondary" size="sm" onClick={() => void refetch()}>
                  {t('app.retry')}
                </Button>
              }
            >
              {t('referrals.load_error')}
            </Banner>
          </div>
        ) : null}

        {isLoading ? (
          <Card>
            <div className="flex flex-col gap-s3" aria-busy>
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          </Card>
        ) : !data || data.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Inbox className="h-7 w-7" />}
              title={searchTerm ? t('referrals.empty_search_title') : t('referrals.empty_title')}
              action={searchTerm ? undefined : newButton}
            />
          </Card>
        ) : (
          <Card className="p-0 nav:p-0">
            <div className="flex flex-wrap gap-x-s4 gap-y-s2 border-b border-ds-line px-s3 py-s3 nav:hidden">
              {sortableColumns.map((column) => (
                <span key={column.id}>{sortHeader(column.id, column.label)}</span>
              ))}
            </div>
            <ResponsiveTable
              columns={columns}
              rows={sortedReferrals}
              getRowKey={(r) => r.id}
              getRowHref={(r) => `/henvisninger/${r.id}`}
              getRowLabel={(r) => `${r.first_name} ${r.last_name}`.trim()}
              empty={null}
              mobileCard={(r) => (
                <div className="w-full text-left">
                  <NameCell referral={r} />
                  <div className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>
                    {r.referrer_name || t('referrals.medcom_badge')}
                    {' · '}
                    <span className={TYPE_TABULAR}>{formatReferralDate(r.received_at) ?? '—'}</span>
                  </div>
                  <div className="mt-s2 flex flex-wrap items-center gap-s2">
                    <ReferralStatusBadge status={r.status} />
                    {r.source === 'medcom' ? <MedComBadge /> : null}
                    <ReferralAttachmentMarker summary={attachmentSummary?.get(r.id)} />
                  </div>
                </div>
              )}
            />
          </Card>
        )}
      </div>
      {medcomOpen && activeClinicId ? (
        <MedcomReferralImportModal clinicId={activeClinicId} onClose={() => setMedcomOpen(false)} />
      ) : null}
    </PageLayout>
  );
}

function NameCell({ referral }: { referral: ReferralListItem }) {
  const name = `${referral.first_name} ${referral.last_name}`.trim();
  const href = `/henvisninger/${referral.id}`;
  return (
    <Link
      to={href}
      tabIndex={-1}
      onClick={stopRowInteractive}
      className={cn(TYPE.body, 'block font-medium text-ds-text')}
    >
      {name}
    </Link>
  );
}

function stopRowInteractive(event: MouseEvent) {
  event.stopPropagation();
}

function ReferrerCell({
  referral,
  summary,
}: {
  referral: ReferralListItem;
  summary: { total: number; received: number } | undefined;
}) {
  const { t } = useTranslation();
  if (referral.referrer_name) {
    return (
      <div>
        <div className="font-medium">{referral.referrer_name}</div>
        {referral.source === 'medcom' && (
          <div className="mt-s1 flex flex-wrap gap-s1">
            <MedComBadge />
            <ReferralAttachmentMarker summary={summary} />
          </div>
        )}
        {referral.referrer_type && (
          <div className={UI_SECONDARY}>
            {t(`referrals.referrer_type_${referral.referrer_type}`)}
          </div>
        )}
      </div>
    );
  }
  if (referral.source === 'medcom') {
    return (
      <div className="flex flex-wrap gap-s1">
        <MedComBadge />
        <ReferralAttachmentMarker summary={summary} />
      </div>
    );
  }
  return <span className="text-ds-muted">—</span>;
}

function MedComBadge() {
  const { t } = useTranslation();
  return <CodeChip>{t('referrals.medcom_badge')}</CodeChip>;
}

export function ReferralStatusBadge({ status }: { status: ReferralListItem['status'] }) {
  const { t } = useTranslation();
  const tone: Record<ReferralStatus, StatusTone> = {
    new: 'upcoming',
    accepted: 'completed',
    declined: 'red',
  };
  return <StatusChip tone={tone[status]}>{t(`referrals.status_${status}`)}</StatusChip>;
}

function InfoCollectionProgress({ referral }: { referral: ReferralListItem }) {
  const fields = [
    referral.consent_collected,
    referral.info_letter_sent,
    referral.agreement_made,
    referral.payment_arranged,
  ];

  const collected = fields.filter(Boolean).length;
  const total = 4;

  return (
    <div className="flex items-center gap-s2">
      <span className={`${UI_SECONDARY} font-medium tabular-nums`}>
        {collected}/{total}
      </span>
      <div className="flex gap-s1">
        {fields.map((isChecked, i) => (
          <div
            key={i}
            className={`h-1.5 w-1.5 rounded-full ${
              isChecked ? 'bg-ds-green' : 'bg-ds-neutral-tint'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function ariaSort(
  sort: ReferralListSort,
  column: ReferralSortColumn,
): 'ascending' | 'descending' | 'none' {
  if (sort.column !== column) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}

function referralsHaveMedcom(rows: ReferralListItem[] | undefined): boolean {
  return (rows ?? []).some((r) => r.source === 'medcom');
}

function ReferralAttachmentMarker({
  summary,
}: {
  summary: { total: number; received: number } | undefined;
}) {
  if (!summary) return null;
  return <AttachmentStatusBadge total={summary.total} received={summary.received} />;
}
